import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { format, startOfMonth, endOfMonth, subMonths, differenceInDays, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { 
  Plus, 
  Search, 
  X, 
  Check, 
  Pencil, 
  Trash2,
  CalendarIcon,
  Clock,
  DollarSign,
  AlertTriangle,
  Calendar,
  ArrowRight,
  FilterX
} from 'lucide-react'
import { DashboardLayout } from '@/components/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { 
  usePayments, 
  usePaymentSummary, 
  useMarkPaymentAsPaid, 
  useDeletePayment,
  Payment,
  PaymentStatus,
  PaymentMethod,
  PaymentFilters,
} from '@/hooks/usePayments'
import { useProjects } from '@/hooks/useProjects'
import { useClients } from '@/hooks/useClients'
import { PaymentDialog } from '@/components/payments/PaymentDialog'
import { PaymentStatusBadge } from '@/components/payments/PaymentStatusBadge'
import { PaymentMethodIcon } from '@/components/payments/PaymentMethodIcon'

type PeriodPreset = 'none' | 'this-month' | 'last-month' | 'last-3-months'

const periodPresets: { id: PeriodPreset; label: string; getRange: () => { from: Date; to: Date } }[] = [
  { 
    id: 'this-month', 
    label: 'Este mês', 
    getRange: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }) 
  },
  { 
    id: 'last-month', 
    label: 'Mês anterior', 
    getRange: () => ({ from: startOfMonth(subMonths(new Date(), 1)), to: endOfMonth(subMonths(new Date(), 1)) }) 
  },
  { 
    id: 'last-3-months', 
    label: 'Últimos 3 meses', 
    getRange: () => ({ from: startOfMonth(subMonths(new Date(), 2)), to: endOfMonth(new Date()) }) 
  },
]

export default function PaymentsPage() {
  const [activeTab, setActiveTab] = useState<string>('all')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [projectSearch, setProjectSearch] = useState('')
  
  // Period filter - starts with NO period (show all)
  const [activePeriod, setActivePeriod] = useState<PeriodPreset>('none')
  const [selectedClient, setSelectedClient] = useState<string | null>(null)
  const [selectedMethods, setSelectedMethods] = useState<PaymentMethod[]>([])
  
  // Compute date range from active period
  const dateRange = useMemo(() => {
    if (activePeriod === 'none') return { from: undefined, to: undefined }
    const preset = periodPresets.find(p => p.id === activePeriod)
    if (!preset) return { from: undefined, to: undefined }
    const range = preset.getRange()
    return { from: range.from, to: range.to }
  }, [activePeriod])
  
  // Build filters object
  const filters: PaymentFilters = useMemo(() => ({
    status: activeTab !== 'all' ? (activeTab as PaymentStatus) : null,
    project_id: null,
    client_id: selectedClient,
    payment_method: selectedMethods.length > 0 ? selectedMethods : null,
    start_date: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : null,
    end_date: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : null,
  }), [activeTab, selectedClient, selectedMethods, dateRange])
  
  const { data: payments, isLoading } = usePayments(filters)
  const { data: summary, isLoading: summaryLoading } = usePaymentSummary()
  const { data: projects } = useProjects()
  const { data: clients } = useClients()
  const markAsPaid = useMarkPaymentAsPaid()
  const deletePayment = useDeletePayment()
  
  // Filter by project name search (client-side)
  const filteredPayments = useMemo(() => {
    if (!payments) return []
    if (!projectSearch.trim()) return payments
    
    const searchLower = projectSearch.toLowerCase()
    return payments.filter(p => 
      p.projects?.name.toLowerCase().includes(searchLower) ||
      p.description?.toLowerCase().includes(searchLower) ||
      p.projects?.clients?.name.toLowerCase().includes(searchLower) ||
      p.invoice_number?.toLowerCase().includes(searchLower)
    )
  }, [payments, projectSearch])
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(value)
  }
  
  const getDaysUntilDue = (dueDate: string | null) => {
    if (!dueDate) return null
    return differenceInDays(parseISO(dueDate), new Date())
  }
  
  const formatDueDate = (dueDate: string | null, status: PaymentStatus) => {
    if (!dueDate) return '-'
    
    const days = getDaysUntilDue(dueDate)
    const formattedDate = format(parseISO(dueDate), 'dd/MM/yyyy')
    
    if (status === 'paid' || status === 'cancelled') {
      return formattedDate
    }
    
    if (days === null) return formattedDate
    
    if (days < 0) {
      return (
        <span className="text-destructive font-medium">
          {formattedDate} ({Math.abs(days)}d atrasado)
        </span>
      )
    }
    
    if (days === 0) {
      return (
        <span className="text-orange-600 font-medium">
          {formattedDate} (Hoje)
        </span>
      )
    }
    
    if (days <= 7) {
      return (
        <span className="text-yellow-600 font-medium">
          {formattedDate} ({days}d)
        </span>
      )
    }
    
    return formattedDate
  }
  
  // When user types in project search, clear period filter
  const handleProjectSearch = (value: string) => {
    setProjectSearch(value)
    if (value.trim()) {
      setActivePeriod('none')
    }
  }
  
  // Select a period preset
  const handlePeriodSelect = (period: PeriodPreset) => {
    if (activePeriod === period) {
      setActivePeriod('none')
    } else {
      setActivePeriod(period)
      // Clear project search when selecting a period
      setProjectSearch('')
    }
  }
  
  const clearAllFilters = () => {
    setActivePeriod('none')
    setSelectedClient(null)
    setSelectedMethods([])
    setProjectSearch('')
    setActiveTab('all')
  }
  
  const hasActiveFilters = activePeriod !== 'none' || selectedClient || selectedMethods.length > 0 || projectSearch.trim() || activeTab !== 'all'
  
  const handleEdit = (payment: Payment) => {
    setEditingPayment(payment)
    setDialogOpen(true)
  }
  
  const handleDelete = async () => {
    if (deleteConfirmId) {
      await deletePayment.mutateAsync(deleteConfirmId)
      setDeleteConfirmId(null)
    }
  }
  
  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      setEditingPayment(null)
    }
  }
  
  // Mobile card renderer
  const PaymentCard = ({ payment }: { payment: Payment }) => {
    return (
      <Card className="shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="font-semibold">{formatCurrency(Number(payment.amount))}</p>
              <p className="text-sm text-muted-foreground line-clamp-1">
                {payment.description || payment.projects?.name || 'Sem descrição'}
              </p>
            </div>
            <PaymentStatusBadge status={payment.status} pulse={payment.status === 'overdue'} />
          </div>
          
          {payment.projects && (
            <div className="text-sm">
              <Link 
                to={`/projects/${payment.projects.id}`}
                className="text-primary hover:underline"
              >
                {payment.projects.name}
              </Link>
              {payment.projects.clients && (
                <span className="text-muted-foreground"> • {payment.projects.clients.name}</span>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4 text-muted-foreground">
              {payment.due_date && (
                <span className="flex items-center gap-1">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {formatDueDate(payment.due_date, payment.status)}
                </span>
              )}
              <PaymentMethodIcon method={payment.payment_method} />
            </div>
          </div>
          
          <div className="flex gap-2 pt-2 border-t">
            {(payment.status === 'pending' || payment.status === 'overdue') && (
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={() => markAsPaid.mutate(payment.id)}
                disabled={markAsPaid.isPending}
              >
                <Check className="h-4 w-4 mr-1" />
                Marcar Pago
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleEdit(payment)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleteConfirmId(payment.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }
  
  return (
    <DashboardLayout title="Pagamentos">
      <div className="space-y-6">
        {/* Header with CTA */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Pagamentos</h1>
            <p className="text-muted-foreground">Gerencie seus recebimentos e cobranças</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="gap-2" size="lg">
            <Plus className="h-5 w-5" />
            Novo Pagamento
          </Button>
        </div>
        
        {/* Summary Cards */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {summaryLoading ? (
            <>
              <Skeleton className="h-28" />
              <Skeleton className="h-28" />
              <Skeleton className="h-28" />
              <Skeleton className="h-28" />
            </>
          ) : (
            <>
              <Card 
                className={cn(
                  "shadow-sm cursor-pointer transition-all hover:shadow-md",
                  activeTab === 'pending' && "ring-2 ring-yellow-500"
                )}
                onClick={() => setActiveTab(activeTab === 'pending' ? 'all' : 'pending')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-yellow-500/20 p-3">
                      <Clock className="h-6 w-6 text-yellow-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pendente</p>
                      <p className="text-2xl font-bold text-yellow-600">{formatCurrency(summary?.total_pending || 0)}</p>
                      <p className="text-xs text-muted-foreground">{summary?.count_pending || 0} pagamentos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className={cn(
                  "shadow-sm cursor-pointer transition-all hover:shadow-md",
                  activeTab === 'paid' && "ring-2 ring-green-500"
                )}
                onClick={() => setActiveTab(activeTab === 'paid' ? 'all' : 'paid')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-green-500/20 p-3">
                      <DollarSign className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Recebido</p>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(summary?.total_paid || 0)}</p>
                      <p className="text-xs text-muted-foreground">{summary?.count_paid || 0} pagamentos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className={cn(
                  "shadow-sm cursor-pointer transition-all hover:shadow-md",
                  activeTab === 'overdue' && "ring-2 ring-red-500",
                  (summary?.count_overdue || 0) > 0 && "bg-red-50 dark:bg-red-950/20 border-red-200"
                )}
                onClick={() => setActiveTab(activeTab === 'overdue' ? 'all' : 'overdue')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "rounded-full p-3",
                      (summary?.count_overdue || 0) > 0 
                        ? "bg-red-500/20 animate-pulse" 
                        : "bg-red-500/10"
                    )}>
                      <AlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-red-600 uppercase tracking-wide">
                        {(summary?.count_overdue || 0) > 0 ? '⚠️ Atrasado' : 'Atrasado'}
                      </p>
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(summary?.total_overdue || 0)}</p>
                      <p className="text-xs text-muted-foreground">{summary?.count_overdue || 0} pagamentos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-blue-500/20 p-3">
                      <Calendar className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Próx. 7 dias</p>
                      <p className="text-2xl font-bold text-blue-600">{formatCurrency(summary?.total_upcoming || 0)}</p>
                      <p className="text-xs text-muted-foreground">{summary?.count_upcoming || 0} vencendo</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
        
        {/* Quick Actions for overdue payments */}
        {(summary?.count_overdue || 0) > 0 && activeTab !== 'overdue' && (
          <Card className="border-orange-200 bg-orange-50/50 dark:bg-orange-950/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="font-medium text-orange-800 dark:text-orange-200">
                      Você tem {summary?.count_overdue} pagamento(s) em atraso
                    </p>
                    <p className="text-sm text-orange-600">
                      Totalizando {formatCurrency(summary?.total_overdue || 0)}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="border-orange-300 text-orange-700 hover:bg-orange-100"
                  onClick={() => setActiveTab('overdue')}
                >
                  Ver Atrasados
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Filters Section */}
        <Card className="shadow-sm">
          <CardContent className="p-4 space-y-4">
            {/* Row 1: Search + Period presets */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {/* Project search */}
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por projeto, cliente..."
                  value={projectSearch}
                  onChange={(e) => handleProjectSearch(e.target.value)}
                  className="pl-9"
                />
                {projectSearch && (
                  <button
                    onClick={() => setProjectSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              
              {/* Period preset chips */}
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-muted-foreground mr-1 hidden sm:inline">Período:</span>
                {periodPresets.map((preset) => (
                  <Button
                    key={preset.id}
                    variant={activePeriod === preset.id ? "default" : "outline"}
                    size="sm"
                    className={cn(
                      "h-7 text-xs px-3 rounded-full transition-all",
                      activePeriod === preset.id && "shadow-sm"
                    )}
                    onClick={() => handlePeriodSelect(preset.id)}
                  >
                    {activePeriod === preset.id && <Check className="h-3 w-3 mr-1" />}
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Row 2: Additional filters + Clear */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Client Filter */}
              <Select value={selectedClient || 'all'} onValueChange={(v) => setSelectedClient(v === 'all' ? null : v)}>
                <SelectTrigger className="w-[160px] h-8 text-xs">
                  <SelectValue placeholder="Cliente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os clientes</SelectItem>
                  {clients?.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Status tabs inline */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
                <TabsList className="h-8 bg-muted/50 p-0.5">
                  <TabsTrigger value="all" className="h-7 text-xs px-3 data-[state=active]:bg-background">
                    Todos
                  </TabsTrigger>
                  <TabsTrigger value="pending" className="h-7 text-xs px-3 gap-1 data-[state=active]:bg-background">
                    Pendentes
                    {(summary?.count_pending || 0) > 0 && (
                      <Badge variant="secondary" className="ml-0.5 h-4 px-1 text-[10px] bg-yellow-100 text-yellow-700">
                        {summary?.count_pending}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="paid" className="h-7 text-xs px-3 data-[state=active]:bg-background">
                    Pagos
                  </TabsTrigger>
                  <TabsTrigger value="overdue" className="h-7 text-xs px-3 gap-1 data-[state=active]:bg-background">
                    Atrasados
                    {(summary?.count_overdue || 0) > 0 && (
                      <Badge variant="destructive" className="ml-0.5 h-4 px-1 text-[10px] animate-pulse">
                        {summary?.count_overdue}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              
              {/* Clear all filters */}
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearAllFilters} 
                  className="h-8 text-xs gap-1.5 text-muted-foreground hover:text-destructive"
                >
                  <FilterX className="h-3.5 w-3.5" />
                  Limpar filtros
                </Button>
              )}
            </div>

            {/* Active filters summary */}
            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className="text-muted-foreground">Filtrando:</span>
                {activePeriod !== 'none' && dateRange.from && dateRange.to && (
                  <Badge variant="secondary" className="gap-1 h-5 text-[11px] font-normal">
                    <CalendarIcon className="h-3 w-3" />
                    {format(dateRange.from, 'dd/MM', { locale: ptBR })} - {format(dateRange.to, 'dd/MM', { locale: ptBR })}
                    <button onClick={() => setActivePeriod('none')} className="ml-0.5 hover:text-destructive">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                )}
                {projectSearch && (
                  <Badge variant="secondary" className="gap-1 h-5 text-[11px] font-normal">
                    <Search className="h-3 w-3" />
                    "{projectSearch}"
                    <button onClick={() => setProjectSearch('')} className="ml-0.5 hover:text-destructive">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                )}
                {activeTab !== 'all' && (
                  <Badge variant="secondary" className="gap-1 h-5 text-[11px] font-normal">
                    Status: {activeTab === 'pending' ? 'Pendente' : activeTab === 'paid' ? 'Pago' : 'Atrasado'}
                    <button onClick={() => setActiveTab('all')} className="ml-0.5 hover:text-destructive">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                )}
                {selectedClient && (
                  <Badge variant="secondary" className="gap-1 h-5 text-[11px] font-normal">
                    Cliente: {clients?.find(c => c.id === selectedClient)?.name}
                    <button onClick={() => setSelectedClient(null)} className="ml-0.5 hover:text-destructive">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Payments List/Table */}
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-lg font-medium">Nenhum pagamento encontrado</p>
            <p className="text-muted-foreground text-sm">
              {hasActiveFilters ? 'Tente ajustar os filtros' : 'Registre seu primeiro pagamento'}
            </p>
            {hasActiveFilters && (
              <Button variant="link" onClick={clearAllFilters} className="mt-2 gap-1">
                <FilterX className="h-4 w-4" />
                Limpar filtros
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Result count */}
            <p className="text-xs text-muted-foreground">
              {filteredPayments.length} pagamento{filteredPayments.length !== 1 ? 's' : ''} encontrado{filteredPayments.length !== 1 ? 's' : ''}
            </p>
            
            {/* Mobile: Cards */}
            <div className="grid gap-3 md:hidden">
              {filteredPayments.map((payment) => (
                <PaymentCard key={payment.id} payment={payment} />
              ))}
            </div>
            
            {/* Desktop: Table */}
            <div className="hidden md:block rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Status</TableHead>
                    <TableHead>Projeto</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Pago em</TableHead>
                    <TableHead>Forma</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        <PaymentStatusBadge 
                          status={payment.status} 
                          size="sm"
                          pulse={payment.status === 'overdue'}
                        />
                      </TableCell>
                      <TableCell>
                        {payment.projects ? (
                          <Link 
                            to={`/projects/${payment.projects.id}`}
                            className="text-primary hover:underline font-medium"
                          >
                            {payment.projects.name}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                        {payment.projects?.clients && (
                          <p className="text-xs text-muted-foreground">
                            {payment.projects.clients.name}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <p className="truncate">{payment.description || '-'}</p>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(Number(payment.amount))}
                      </TableCell>
                      <TableCell>
                        {formatDueDate(payment.due_date, payment.status)}
                      </TableCell>
                      <TableCell>
                        {payment.paid_date 
                          ? format(parseISO(payment.paid_date), 'dd/MM/yyyy')
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <PaymentMethodIcon method={payment.payment_method} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {(payment.status === 'pending' || payment.status === 'overdue') && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => markAsPaid.mutate(payment.id)}
                              disabled={markAsPaid.isPending}
                              title="Marcar como pago"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => handleEdit(payment)}
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => setDeleteConfirmId(payment.id)}
                            title="Excluir"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
      
      {/* Payment Dialog */}
      <PaymentDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        payment={editingPayment}
      />
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir pagamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O pagamento será removido permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  )
}
